/**
 * Print Class Lists Page
 * Allows teachers to select multiple classes and print their class lists
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Button, Alert, CircularProgress,
  Breadcrumbs, Link as MuiLink, Card,
  List, ListItem, ListItemButton, ListItemText,
  Checkbox, Stack, Chip
} from '@mui/material';
import {
  Home, Print, Check, Clear
} from '@mui/icons-material';
import axios from 'axios';

interface Learner {
  id: number;
  student_id: string;
  first_name: string;
  last_name: string;
  full_name: string;
  email: string;
}

interface ClassData {
  id: number;
  name: string;
  grade_level: number;
  subject: string;
  teacher_name: string;
  learners?: Learner[];
}

const PrintClassListsPage: React.FC = () => {
  const navigate = useNavigate();

  // Data state
  const [classes, setClasses] = useState<ClassData[]>([]);
  const [selectedClassIds, setSelectedClassIds] = useState<Set<number>>(new Set());

  // UI state
  const [loading, setLoading] = useState(true);
  const [loadingEnrollments, setLoadingEnrollments] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load all classes
  useEffect(() => {
    const fetchClasses = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await axios.get('/api/school-classes/');
        const classesData = response.data.results || response.data;
        setClasses(classesData);

      } catch (err: any) {
        console.error('Error loading classes:', err);
        setError(err.response?.data?.error || 'Failed to load classes');
      } finally {
        setLoading(false);
      }
    };

    fetchClasses();
  }, []);

  // Toggle class selection
  const toggleClass = (classId: number) => {
    const newSet = new Set(selectedClassIds);
    if (newSet.has(classId)) {
      newSet.delete(classId);
    } else {
      newSet.add(classId);
    }
    setSelectedClassIds(newSet);
  };

  // Select all classes
  const selectAll = () => {
    setSelectedClassIds(new Set(classes.map(c => c.id)));
  };

  // Deselect all classes
  const deselectAll = () => {
    setSelectedClassIds(new Set());
  };

  // Print selected classes
  const handlePrintSelected = async () => {
    if (selectedClassIds.size === 0) {
      setError('Please select at least one class to print');
      return;
    }

    try {
      setLoadingEnrollments(true);
      setError(null);

      // Fetch enrollment data for each selected class
      const selectedClasses = classes.filter(c => selectedClassIds.has(c.id));
      const classesWithEnrollments = await Promise.all(
        selectedClasses.map(async (classData) => {
          const response = await axios.get(`/api/school-classes/${classData.id}/enrollments/`);
          return {
            ...classData,
            learners: response.data.learners
          };
        })
      );

      // Generate print content for all selected classes
      const now = new Date();
      const timestamp = now.toLocaleString('en-ZA', {
        hour: '2-digit',
        minute: '2-digit',
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });

      const printContent = `
        <html>
          <head>
            <title>Class Lists</title>
            <style>
              @media print {
                @page { margin: 2cm; }
                .page-break { page-break-before: always; }
              }
              body {
                font-family: Arial, sans-serif;
                padding: 20px;
                max-width: 800px;
                margin: 0 auto;
              }
              .class-list {
                margin-bottom: 40px;
              }
              h1 {
                font-size: 18px;
                margin-bottom: 5px;
                font-weight: bold;
              }
              h2 {
                font-size: 16px;
                margin: 5px 0;
                font-weight: normal;
              }
              table {
                width: 100%;
                border-collapse: collapse;
                margin: 20px 0;
              }
              th {
                text-align: left;
                padding: 8px;
                border-bottom: 1px solid #000;
                font-weight: bold;
              }
              td {
                padding: 8px;
                border-bottom: 1px solid #ddd;
              }
              .footer {
                margin-top: 20px;
                font-size: 14px;
              }
            </style>
          </head>
          <body>
            ${classesWithEnrollments.map((classData, index) => {
              const sortedLearners = [...(classData.learners || [])].sort((a, b) =>
                a.last_name.localeCompare(b.last_name)
              );

              return `
                <div class="class-list ${index > 0 ? 'page-break' : ''}">
                  <h1>School Name</h1>
                  <h2>Scratch List</h2>
                  <h2>${classData.subject} Graad ${classData.grade_level} - ${classData.name}</h2>
                  ${classData.teacher_name ? `<h2>(${classData.teacher_name})</h2>` : ''}

                  <table>
                    <thead>
                      <tr>
                        <th>Last Name</th>
                        <th>Preferred Name</th>
                      </tr>
                    </thead>
                    <tbody>
                      ${sortedLearners.map(learner => `
                        <tr>
                          <td>${learner.last_name}</td>
                          <td>${learner.first_name}</td>
                        </tr>
                      `).join('')}
                    </tbody>
                  </table>

                  <div class="footer">
                    Total: ${sortedLearners.length} people, generated at ${timestamp}
                  </div>
                </div>
              `;
            }).join('')}
          </body>
        </html>
      `;

      // Open print window
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(printContent);
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => {
          printWindow.print();
        }, 250);
      }

    } catch (err: any) {
      console.error('Error loading enrollments:', err);
      setError(err.response?.data?.error || 'Failed to load class enrollments');
    } finally {
      setLoadingEnrollments(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
        <MuiLink
          component="button"
          variant="body1"
          onClick={() => navigate('/dashboard')}
          sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.5 }}
        >
          <Home fontSize="small" />
          Dashboard
        </MuiLink>
        <MuiLink
          component="button"
          variant="body1"
          onClick={() => navigate('/classes')}
          sx={{ cursor: 'pointer' }}
        >
          Classes
        </MuiLink>
        <Typography color="text.primary">
          Print Class Lists
        </Typography>
      </Breadcrumbs>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Header */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="start">
          <Box>
            <Typography variant="h4" gutterBottom>
              <Print sx={{ mr: 1, verticalAlign: 'middle' }} />
              Print Class Lists
            </Typography>
            <Typography variant="body1" color="textSecondary">
              Select classes to print their scratch lists
            </Typography>
          </Box>
          <Button
            variant="outlined"
            onClick={() => navigate('/classes')}
          >
            Back to Classes
          </Button>
        </Box>
      </Paper>

      {/* Class Selection */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">
            Select Classes ({selectedClassIds.size} selected)
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              startIcon={<Check />}
              onClick={selectAll}
              variant="outlined"
            >
              Select All
            </Button>
            <Button
              size="small"
              startIcon={<Clear />}
              onClick={deselectAll}
              variant="outlined"
            >
              Deselect All
            </Button>
          </Stack>
        </Box>

        {classes.length === 0 ? (
          <Alert severity="info">No classes found.</Alert>
        ) : (
          <List>
            {classes.map((classData) => (
              <Card key={classData.id} variant="outlined" sx={{ mb: 1 }}>
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => toggleClass(classData.id)}
                    selected={selectedClassIds.has(classData.id)}
                  >
                    <Checkbox
                      edge="start"
                      checked={selectedClassIds.has(classData.id)}
                      tabIndex={-1}
                      disableRipple
                    />
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {classData.name}
                          </Typography>
                          <Chip label={`Grade ${classData.grade_level}`} size="small" color="primary" variant="outlined" />
                          <Chip label={classData.subject} size="small" color="secondary" variant="outlined" />
                        </Box>
                      }
                      secondary={classData.teacher_name ? `Teacher: ${classData.teacher_name}` : undefined}
                    />
                  </ListItemButton>
                </ListItem>
              </Card>
            ))}
          </List>
        )}
      </Paper>

      {/* Print Button */}
      <Box display="flex" justifyContent="center">
        <Button
          variant="contained"
          color="primary"
          size="large"
          startIcon={<Print />}
          onClick={handlePrintSelected}
          disabled={selectedClassIds.size === 0 || loadingEnrollments}
        >
          {loadingEnrollments ? 'Loading...' : `Print ${selectedClassIds.size} Class ${selectedClassIds.size === 1 ? 'List' : 'Lists'}`}
        </Button>
      </Box>
    </Box>
  );
};

export default PrintClassListsPage;
